import {
  websocket,
  ConnectionMessageType,
  WebsocketMessage,
  SystemMessageType,
  PeerIdType,
  PeerMessageType,
} from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import { handleAuthMessage } from './handlers/auth';
import { handleConnectionMessage } from './handlers/connection';
import { handlePeerMessage, getStoredPeerData } from './handlers/peer';
import { attachSentryTraceToMessage } from '../realtime/tracing';

const HEARTBEAT_INTERVAL = 10000; // 10 seconds between pings
const RECONNECT_TIMEOUT = 3000; // 3 seconds before reconnecting

let wsConnection: WebSocket | null = null;
let isReady = false;
let messageQueue: WebsocketMessage[] = [];
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let isReconnecting = false;

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

const startHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }

  heartbeatInterval = setInterval(() => {
    if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
      stopHeartbeat();
      return;
    }

    try {
      const pingMessage = websocket.generators.system(SystemMessageType.SYS_PING, {});
      attachSentryTraceToMessage(pingMessage);
      wsConnection.send(JSON.stringify(pingMessage));
    } catch (error) {
      console.error('[Realtime] Failed to send heartbeat:', error);
      disconnect();
      handleReconnect();
    }
  }, HEARTBEAT_INTERVAL);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const handleReconnect = () => {
  // Prevent multiple simultaneous reconnection attempts
  if (isReconnecting) {
    console.log('[Realtime] Reconnection already in progress');
    return;
  }

  isReconnecting = true;

  // Clean up existing connection if it exists
  disconnect();

  // Wait before attempting to reconnect
  clearReconnectTimeout();
  reconnectTimeout = setTimeout(() => {
    try {
      // Reset wsConnection to null before attempting to reconnect
      wsConnection = null;
      connect();
    } catch (error) {
      console.error('[Realtime] Reconnection attempt failed:', error);
    } finally {
      isReconnecting = false;
    }
  }, RECONNECT_TIMEOUT);
};

export const connect = () => {
  if (wsConnection?.readyState === WebSocket.OPEN || wsConnection?.readyState === WebSocket.CONNECTING) {
    console.warn('[Realtime] Connection already exists');
    return;
  }

  try {
    // Pre-load stored peer data for reconnection if available
    const storedData = getStoredPeerData(PeerIdType.REMOTE_HOST);
    if (storedData) {
      console.log('[RemoteHost] Found stored remote host peer data for reconnection');
    }

    wsConnection = new WebSocket(constants.REALTIME_API_WEBSOCKET_URL);

    wsConnection.onopen = () => {
      // Always wait for the next tick to ensure the connection is fully established
      setTimeout(() => {
        if (wsConnection?.readyState === WebSocket.OPEN) {
          console.log('[Realtime] Connected, sending init message');
          const msg = websocket.generators.connection(ConnectionMessageType.CONN_INIT, {
            version: constants.VERSION,
          });
          attachSentryTraceToMessage(msg);
          wsConnection?.send(JSON.stringify(msg));
          startHeartbeat();
        } else {
          console.warn('[Realtime] Connection not ready after open event');
          handleReconnect();
        }
      }, 0);
    };

    wsConnection.onmessage = (event) => {
      const validation = websocket.validateWebsocketMessage(event.data);
      if (!validation.success) {
        console.error('[Realtime] Invalid message:', validation.error);
        return;
      }

      const message = validation.data;
      if (!message) {
        console.error('[Realtime] No message data');
        return;
      }

      try {
        if (websocket.isConnectionMessage(message)) {
          handleConnectionMessage(message, wsConnection);
        } else if (websocket.isAuthMessage(message)) {
          handleAuthMessage(message);
        } else if (websocket.isPeerMessage(message)) {
          handlePeerMessage(message);
        }
      } catch (e) {
        console.error('[Realtime] Failed to handle message:', e);
      }
    };

    wsConnection.onclose = () => {
      console.log('[Realtime] WebSocket closed');
      isReady = false;
      stopHeartbeat();

      // Only attempt reconnect if we weren't already trying to disconnect
      if (!isReconnecting) {
        handleReconnect();
      }
    };

    wsConnection.onerror = (error) => {
      console.error('[Realtime] WebSocket error:', error);
      // Don't call handleReconnect here - let onclose handle it
    };
  } catch (error) {
    console.error('[Realtime] Failed to connect:', error);
    handleReconnect();
  }
};

const processQueue = () => {
  while (messageQueue.length > 0 && isReady && wsConnection?.readyState === WebSocket.OPEN) {
    const message = messageQueue.shift();
    if (message) {
      try {
        attachSentryTraceToMessage(message);
        wsConnection.send(JSON.stringify(message));
      } catch (error) {
        console.error('[Realtime] Failed to send queued message:', error);
        messageQueue.unshift(message); // Put the message back at the start of the queue
        break;
      }
    }
  }
};

export const disconnect = () => {
  stopHeartbeat();
  clearReconnectTimeout();
  isReady = false;
  messageQueue = [];

  if (wsConnection) {
    // Store reference and null the connection before closing
    const conn = wsConnection;
    wsConnection = null;

    try {
      conn.close();
    } catch (error) {
      console.error('[Realtime] Error while closing connection:', error);
    }
  }
};

export const setIsReady = (ready: boolean) => {
  isReady = ready;
  if (isReady) {
    processQueue();
  }
};

export const sendMessage = (message: WebsocketMessage) => {
  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN || !isReady) {
    console.warn('[Realtime] Connection not ready, queueing message');
    messageQueue.push(message);
    return;
  }

  try {
    attachSentryTraceToMessage(message);
    wsConnection.send(JSON.stringify(message));
  } catch (error) {
    console.error('[Realtime] Failed to send message:', error);
    messageQueue.push(message);
  }
};

// New function to request peer ID with potential reconnection
export const requestPeerId = (peerIdType: PeerIdType) => {
  // Check if we have stored peer data for reconnection
  const storedData = getStoredPeerData(peerIdType);

  if (storedData && wsConnection?.readyState === WebSocket.OPEN) {
    console.log(`[RemoteHost] Attempting to reclaim peer ID for ${peerIdType}`, storedData.peerId);
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
        peerIdType,
        peerId: storedData.peerId,
        peerToken: storedData.peerToken,
      }),
    );
  } else {
    // Request a new peer ID if none is stored or connection isn't open
    console.log(`[RemoteHost] Requesting new peer ID for ${peerIdType}`);
    sendMessage(websocket.generators.peer(PeerMessageType.PEER_GET_ID, { peerIdType }));
  }
};
